<template>
  <div class="row">
    <div class="col-12">
      <page-header />
      <div
        v-if="hasAdminAccess"
        class="row page-section"
      >
        <div class="col-12 main-content">
          <validated-form :submit="onSubmit">
            <template v-slot:form>
              <div class="row">
                <div class="col-12">
                  <validated-multifile-input
                    v-model="formData.files"
                    :label="'files' | i18n"
                    :name="'files' | i18n"
                    :multiple="true"
                    :disabled.sync="isImportInProgress"
                    input-id="import-file"
                    label-for="import-file-input"
                    file-extensions=".asc, .xml"
                    rules="required"
                  />
                </div>
              </div>
              <div class="row">
                <div class="col-12 col-lg-6">
                  <b-form-checkbox
                    v-model="formData.excludeFirstLine"
                  >
                    {{ 'exclude_first_line_asc' | i18n }}
                  </b-form-checkbox>
                </div>
                <div class="col-12 col-lg-6">
                  <b-form-group>
                    <label
                      for="file-encoding-input"
                      class="d-inline-block mb-0"
                    >{{ 'file_encoding' | i18n }}: </label>
                    <b-form-radio-group
                      id="file-encoding-input"
                      v-model="formData.fileEncoding"
                      :options="fileEncodingOptions"
                      name="file-encoding"
                      plain
                    />
                  </b-form-group>
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  <p class="help-text mb-0">
                    {{ 'file_encoding_warning' | i18n({fileEncoding: formData.fileEncoding}) }}
                  </p>
                </div>
              </div>
            </template>
            <template v-slot:buttons>
              <div
                id="button-import-container"
                class="float-right"
                @mouseover="showTooltip = true"
              >
                <b-button
                  type="submit"
                  size="sm"
                  variant="primary"
                  :disabled="isImportInProgress || !formData.files.length"
                >
                  {{ 'upload' | i18n }}
                </b-button>
                <b-tooltip
                  :show.sync="showTooltip"
                  target="button-import-container"
                  placement="top"
                >
                  {{ (importInProgress ? 'import_in_progress' : 'import') | i18n }}
                </b-tooltip>
              </div>
            </template>
          </validated-form>
        </div>
      </div>
      <div class="row page-section">
        <div class="col-12">
          <div class="row section-header">
            <div class="col-12 col-md-6">
              {{ 'imports' | i18n }}
            </div>
            <div class="col-12 col-md-6">
              <div class="action-buttons">
                <b-button
                  v-b-tooltip.hover
                  size="sm"
                  variant="outline-primary"
                  :title="'refresh' | i18n"
                  @click="refresh"
                >
                  <BIconArrowRepeat />
                </b-button>
                <b-button
                  size="sm"
                  variant="outline-primary"
                  :disabled="!table.selectedRows.length"
                  @click="deleteImportRequests"
                >
                  {{ 'delete' | i18n }}
                </b-button>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-12 main-content">
              <data-table
                ref="table"
                v-model="table"
                :get-page="getPage"
                :has-details-row="true"
              >
                <template v-slot:cell(createdAt)="row">
                  {{ row.item.createdAt | formatDate('DD.MM.YY HH:mm') }}
                </template>
                <template v-slot:cell(completedAt)="row">
                  {{ row.item.completedAt | formatDate('DD.MM.YY HH:mm') }}
                </template>
                <template v-slot:cell(isSuccessful)="row">
                  <b-icon
                    v-if="row.item.isCompleted"
                    v-b-tooltip.hover
                    :title="row.item.isSuccessful ? translate('successful') :
                      (row.detailsShowing ? translate('error_during_import') : `${translate('error_during_import')}. ${translate('click_for_details')}`)"
                    :icon="row.item.isSuccessful ? 'check-circle' : 'x-circle'"
                    :variant="row.item.isSuccessful ? 'success' : 'danger'"
                    class="cursor-pointer"
                    @click="showErrorDetails(row)"
                  />
                  <b-icon
                    v-if="!row.item.isCompleted"
                    v-b-tooltip.hover
                    :title="'in_progress' | i18n"
                    icon="arrow-clockwise"
                    animation="spin"
                    class="cursor-pointer"
                  />
                </template>
                <template v-slot:row-details="row">
                  <div class="row details-row">
                    <div
                      v-if="row.item.isCompleted && row.item.processingErrors.length"
                      class="col-12"
                    >
                      <span class="entity-result">
                        <b-icon
                          icon="x-circle"
                          variant="danger"
                        />
                        <span class="ml-2">Error: {{ row.item.processingErrors.join(', ') }}</span>
                      </span>
                    </div>
                    <div
                      v-for="entityResult of row.item.entityResults"
                      :key="entityResult.fileOriginalName"
                      class="col-12 col-md-6 col-xl-4"
                    >
                      <span class="entity-result">
                        <b-icon
                          v-if="entityResult.isCompleted && !entityResult.errors.length"
                          icon="check-circle"
                          variant="success"
                        />
                        <b-icon
                          v-if="entityResult.isCompleted && entityResult.errors.length"
                          icon="x-circle"
                          variant="danger"
                        />
                        <b-icon
                          v-if="!entityResult.isCompleted"
                          icon="arrow-clockwise"
                          animation="spin"
                        />
                        <span class="ml-2">{{ entityResult.fileOriginalName }}{{ entityResult.errors.length ? `: ${entityResult.errors.join(',')}` : '' }}
                        </span>
                      </span>
                    </div>
                  </div>
                </template>
              </data-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import ValidatedForm from '../forms/validation/validated-form';
import ValidatedMultifileInput from '../forms/validation/validated-multifile-input';
import {ImportEntitiesMixin, NotificationMixin, DeleteRowMixin, LocalStorageMixin, SettingsMixin, AuthMixin} from '../../mixins';
import DataTable from '../table/data-table';
import {BIconArrowRepeat, BIconCheckCircle, BIconXCircle, BIconQuestionCircleFill, BIconArrowClockwise, BFormRadioGroup} from 'bootstrap-vue';
import collapseOpenIcon from '../../assets/icons/collapse-open.svg';
import collapseClosedIcon from '../../assets/icons/collapse-closed.svg';
import {uniq, sortBy} from 'underscore';
import PageHeader from '../structure/page-header';

export default {
  name: 'Import',
  components: {
    PageHeader,
    DataTable,
    ValidatedMultifileInput,
    ValidatedForm,
    BIconArrowRepeat,
    // eslint-disable-next-line vue/no-unused-components
    BIconCheckCircle,
    // eslint-disable-next-line vue/no-unused-components
    BIconXCircle,
    // eslint-disable-next-line vue/no-unused-components
    BIconQuestionCircleFill,
    // eslint-disable-next-line vue/no-unused-components
    BIconArrowClockwise,
    BFormRadioGroup
  },
  mixins: [ImportEntitiesMixin, NotificationMixin, DeleteRowMixin, LocalStorageMixin, SettingsMixin, AuthMixin],
  data: function () {
    return {
      fileEncodingOptions: ['utf8', 'latin1'],
      importDefaultEncoding: 'utf8',
      formData: {
        files: [],
        excludeFirstLine: false,
        fileEncoding: 'utf8'
      },
      table: {
        rows: [],
        selectedRow: null,
        selectedRows: [],
        fields: [
          { key: 'check', label: '', thClass: 'width-10', tdClass: 'width-10'},
          { key: 'entities', label: this.$translate('entities')},
          { key: 'isSuccessful', label: this.$translate('status'), thClass: 'width-7 result-column', tdClass: 'width-7 result-column'},
          { key: 'createdAt', label: this.$translate('created_date'), thClass: 'width-10', tdClass: 'width-10'},
          { key: 'completedAt', label: this.$translate('completed_date'), thClass: 'width-10', tdClass: 'width-10'}
        ]
      },
      isImportInProgress: false,
      showTooltip: false,
      collapseOpenIcon,
      collapseClosedIcon,
      translate: this.$translate,
      watchProgressIntervalId: null,
      importInProgress: null
    };
  },
  created() {
    this.setDefaultEncoding();
    this.checkImportInProgress();
  },
  methods: {
    async setDefaultEncoding() {
      try {
        let importDefaultEncoding = this.localStorageService.get('importDefaultEncoding');
        if (!importDefaultEncoding) {
          const response = await this.settingsService.getSettings();
          importDefaultEncoding = response.importDefaultEncoding;
          this.localStorageService.set('importDefaultEncoding', importDefaultEncoding);
        }

        this.formData.fileEncoding = this.importDefaultEncoding = importDefaultEncoding;
      } catch (err) {
        this.formData.fileEncoding = this.importDefaultEncoding;
      }
    },
    async checkImportInProgress() {
      try {
        const response = await this.importEntitiesService.getImportInProgress();
        if (response.importInProgress) {
          await this.updateProgress(response.importInProgress);
          this.initImportWatcher();
        }
      } catch (err) {
        this.showNotification({type: 'error', message: err.message});
      }
    },
    async onSubmit() {
      try {
        const formData = this.buildFormData();
        await this.importEntitiesService.upload(formData);
        this.showNotification({type: 'success', title: this.$translate('success'), message: this.$translate('success')});
        
        if (this.importDefaultEncoding !== this.formData.fileEncoding) {
          this.importDefaultEncoding = this.formData.fileEncoding;
          await this.settingsService.updateImportDefaultEncoding({importDefaultEncoding: this.importDefaultEncoding});
          this.localStorageService.set('importDefaultEncoding', this.importDefaultEncoding);
        }

        this.resetForm();
        this.initImportWatcher();
        setTimeout(() => this.refresh(), 2000);
      } catch (err) {
        this.showNotification({type: 'error', message: err.message});
      }
    },
    initImportWatcher() {
      this.watchProgressIntervalId = setInterval(async () => {
        await this.updateProgress();
      }, 3000);
    },
    async updateProgress() {
      const response = await this.importEntitiesService.getImportInProgress();

      if (response.importInProgress) {
        this.currentImport = Object.assign({}, this.currentImport, response.importInProgress);
        this.updateTableRow(response.importInProgress);
      } else {
        clearInterval(this.watchProgressIntervalId);

        if (this.currentImport) {
          const response = await this.importEntitiesService.getImportRequestById(this.currentImport.id);
          this.updateTableRow(response.importRequest);
          this.currentImport = null;
          this.showNotification({title: this.$translate('import'), message: this.$translate('import_completed_message'), type: 'success'});
        }
      }
    },
    updateTableRow(importRequest) {
      if (!this.table.rows.length) {
        return;
      }

      let itemIndex = this.table.rows.findIndex(r => r.id === importRequest.id);
      if (itemIndex !== -1) {
        const newValue = this.mapRow(importRequest);
        newValue.isSelected = this.table.rows[itemIndex].isSelected;
        newValue._showDetails = this.table.rows[itemIndex]._showDetails;
        Object.keys(newValue).forEach(key => this.table.rows[itemIndex][key] = newValue[key]);
      }
    },
    async getPage(ctx) {
      try {
        const response = await this.importEntitiesService.getPage(ctx);
        const data = response.importRequests.map(item => this.mapRow(item));
        this.isImportInProgress = response.isImportInProgress;
        return {totalCount: response.totalCount, data: data};
      } catch (err) {
        this.showNotification({type: 'error', message: err.message});
        return {totalCount: 0, data: []};
      }
    },
    mapRow(importRequest) {
      return {
        id: importRequest.id,
        isSuccessful: importRequest.isSuccessful || null,
        isCompleted: importRequest.completedAt,
        fileEncoding: importRequest.fileEncoding,
        createdAt: importRequest.createdAt,
        completedAt: importRequest.completedAt,
        entities: importRequest.entities.length ? uniq(importRequest.entities.map(e => e.name)).sort().join(', ') : this.$translate('unknown'),
        entityResults: sortBy(importRequest.entities.map(entity => ({
          entity: entity.name,
          fileOriginalName: entity.fileOriginalName,
          errors: entity.processingErrors,
          isCompleted: entity.isCompleted
        })) , e => e.entity),
        processingErrors: importRequest.processingErrors,
        isSelected: false,
        _showDetails: false
      };
    },
    refresh() {
      this.$refs.table.refresh();
    },
    buildFormData() {
      const formData = new FormData();
      this.formData.files.forEach(file => {
        formData.append('files', file);
      });
      formData.append('excludeFirstLine', this.formData.excludeFirstLine);
      formData.append('fileEncoding', this.formData.fileEncoding);

      return formData;
    },
    resetForm() {
      this.formData.files = [];
      this.formData.excludeFirstLine = false;
      this.formData.fileEncoding = this.importDefaultEncoding;
    },
    showErrorDetails(row) {
      if (!row.detailsShowing) {
        row.toggleDetails();
      }
    },
    async deleteImportRequests() {
      const selectedItems = this.table.rows.filter(c => c.isSelected);
      await this.deleteSelectedRows({
        message: this.$translate('delete_confirmation_message'),
        rowDisplayValues: []
      }, async () => {
        await this.importEntitiesService.deleteImportRequests({ids: selectedItems.map(item => item.id)});
        this.$refs.table.deselectAll();
        await this.$refs.table.refresh();
      });
    }
  }
};
</script>

<style scoped lang="scss">
  ::v-deep   .b-table-sticky-header {
    max-height: calc(100vh - 550px);
    min-height: calc(100vh - 550px);
  }

  ::v-deep   .table {
    min-width: 900px;

    th, td {
      &.width-7 {
        width: 7%;
      }

      &.width-10 {
        width: 10%;
      }

      &.width-70 {
        width: 70%;
      }

      &.result-column {
        text-align: center;
      }
    }

    .details-row {
      padding: 0 30px;
    }
  }

  ::v-deep   .custom-control.custom-checkbox {
    font-size: 0.9rem;
    padding-left: 0;
  }

  ::v-deep   .form-group {
    margin-bottom: 15px;
    font-size: 0.9rem;
  }

  #file-encoding-input {
    display: inline-block;
    margin-left: 10px;
  }

  ::v-deep   .form-check-inline {
    vertical-align: middle;
  }
</style>
